import { JSX, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import LoadingIndicator from 'components/loading/LoadingIndicator';
import { ROUTES } from 'config/route-configuration';
import { getIdTokenClaims, isAdminUser } from 'utils/auth/adminAuth';

interface RequireAdminProps {
  children: JSX.Element;
}

const RequireAdmin = ({ children }: RequireAdminProps) => {
  const [status, setStatus] = useState<'loading' | 'unauth' | 'forbidden' | 'ok'>('loading');

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const session = await fetchAuthSession();
        const hasToken = !!session.tokens?.idToken;
        if (!hasToken) {
          setStatus('unauth');
          return;
        }
        const claims = await getIdTokenClaims();
        if (!isAdminUser(claims)) {
          await signOut();
          setStatus('forbidden');
          return;
        }
        setStatus('ok');
      } catch {
        setStatus('unauth');
      }
    };

    checkAdmin();
  }, []);

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center py-10">
        <LoadingIndicator />
      </div>
    );
  }

  if (status === 'unauth') {
    return <Navigate to={ROUTES.ADMIN_LOGIN} replace />;
  }

  if (status === 'forbidden') {
    return (
      <div className="min-h-screen bg-[#E6ECEB] px-4 py-12">
        <div className="mx-auto w-full max-w-xl rounded-2xl bg-white p-6 text-center shadow-sm">
          <h1 className="font-mont text-2xl font-black text-brand-black">403 - Not authorized</h1>
          <p className="mt-2 text-sm text-brand-black/70">Your account does not have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return children;
};

export default RequireAdmin;
